import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FaPlus, FaEdit, FaTrash, FaSeedling, FaFileExport, FaFileImport, FaDownload, FaEye, FaCompressArrowsAlt } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { API } from '../App';
import { getErrorMessage } from '../utils/errorMessage';

const AdminIngredientsPage = () => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Sirup',
    category_key: '',
    default_brix: 0,
    keywords: '',
    match_aliases: ''
  });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);  // dry-run preview file
  const [mergeResult, setMergeResult] = useState(null);
  const [merging, setMerging] = useState(false);
  const [showBackupPrompt, setShowBackupPrompt] = useState(false);
  const fileInputRef = useRef(null);
  const dryRunInputRef = useRef(null);

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/ingredients`, {
        withCredentials: true
      });
      setIngredients(response.data);
    } catch (error) {
      console.error('Error fetching ingredients:', error);
      toast.error('Kunne ikke hente ingredienser');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Angiv et navn');
      return;
    }

    // Convert comma-separated match_aliases to an array, trimmed.
    // We also pre-filter obviously-noisy single short words (matches backend rule).
    const aliasArray = (formData.match_aliases || '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const payload = {
      ...formData,
      match_aliases: aliasArray,
    };

    try {
      setSaving(true);
      
      if (editingIngredient) {
        // Update
        await axios.put(`${API}/admin/ingredients/${editingIngredient.id}`, payload, {
          withCredentials: true
        });
        toast.success('Ingrediens opdateret!');
      } else {
        // Create
        await axios.post(`${API}/admin/ingredients`, payload, {
          withCredentials: true
        });
        toast.success('Ingrediens oprettet!');
      }
      
      fetchIngredients();
      setShowDialog(false);
      setEditingIngredient(null);
      setFormData({ name: '', category: 'Sirup', category_key: '', default_brix: 0, keywords: '', match_aliases: '' });
    } catch (error) {
      console.error('Error saving ingredient:', error);
      const detail = getErrorMessage(error, 'Kunne ikke gemme ingrediens');
      toast.error(detail);

      // If the server says it's a duplicate, refresh the list and surface the
      // existing row to the admin so they don't think it's invisible.
      const isDuplicate = error.response?.status === 400 &&
        typeof detail === 'string' &&
        detail.toLowerCase().includes('findes allerede');
      if (isDuplicate) {
        await fetchIngredients();
        // Clear filters so the existing entry is definitely visible
        setSearchQuery('');
        // Try to extract the id from the detail ("(id=...)") and scroll to that row
        const idMatch = detail.match(/id=([a-f0-9-]+)/i);
        const targetId = idMatch ? idMatch[1] : null;
        setTimeout(() => {
          if (targetId) {
            const row = document.querySelector(`[data-ingredient-row="${targetId}"]`);
            if (row) {
              row.scrollIntoView({ behavior: 'smooth', block: 'center' });
              row.classList.add('ring-2', 'ring-orange-400');
              setTimeout(() => row.classList.remove('ring-2', 'ring-orange-400'), 2500);
            }
          }
        }, 300);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Er du sikker på at du vil slette "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/ingredients/${id}`, {
        withCredentials: true
      });
      toast.success('Ingrediens slettet!');
      fetchIngredients();
    } catch (error) {
      console.error('Error deleting ingredient:', error);
      toast.error('Kunne ikke slette ingrediens');
    }
  };

  const handleEdit = (ingredient) => {
    setEditingIngredient(ingredient);
    const aliasesAsString = Array.isArray(ingredient.match_aliases)
      ? ingredient.match_aliases.join(', ')
      : (ingredient.match_aliases || '');
    setFormData({
      name: ingredient.name,
      category: ingredient.category || 'Sirup',
      category_key: ingredient.category_key || '',
      default_brix: ingredient.default_brix || 0,
      keywords: ingredient.keywords || '',
      match_aliases: aliasesAsString
    });
    setShowDialog(true);
  };

  const handleSeedData = async () => {
    if (!window.confirm('Dette vil tilføje standard ingredienser. Fortsæt?')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/ingredients/seed`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchIngredients();
    } catch (error) {
      console.error('Error seeding ingredients:', error);
      toast.error('Kunne ikke tilføje seed data');
    }
  };

  const filteredIngredients = ingredients.filter(ing => {
    const query = searchQuery.toLowerCase();
    return (
      ing.name.toLowerCase().includes(query) ||
      ing.category?.toLowerCase().includes(query) ||
      (ing.keywords && ing.keywords.toLowerCase().includes(query))
    );
  });

  // Trigger a one-shot file download for the given admin CSV endpoint.
  // Set `throwOnError` to true when the caller depends on the download
  // succeeding (e.g. backup-before-destructive-action). Default keeps the
  // legacy fire-and-forget behaviour used by the toolbar buttons.
  const downloadCsv = async (path, filename, throwOnError = false) => {
    try {
      const res = await axios.get(`${API}${path}`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error('Kunne ikke downloade CSV');
      if (throwOnError) throw err;
    }
  };

  const handleExport = () =>
    downloadCsv('/admin/ingredients/export-csv', 'slushbook-ingredients.csv');

  const handleDownloadTemplate = () =>
    downloadCsv('/admin/ingredients/export-csv-template', 'slushbook-ingredients-template.csv');

  const runImport = async (file, { dryRun }) => {
    try {
      setImporting(true);
      setImportResult(null);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dry_run', dryRun ? 'true' : 'false');
      const res = await axios.post(`${API}/admin/ingredients/import-csv`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      const brixCount = res.data.brix_updated_count || 0;
      if (!res.data.detected_brix_header) {
        toast.warning(
          'CSV behandlet, men ingen Brix-kolonne fundet. Tilføj kolonnen "standard_brix" og prøv igen.'
        );
      } else if (dryRun) {
        toast.success(
          `Forhåndsvisning: ${res.data.created} nye · ${res.data.updated} opdateres · ${brixCount} Brix-ændringer (intet skrevet)`
        );
      } else {
        toast.success(
          `Import færdig: ${res.data.created} oprettet · ${res.data.updated} opdateret · ${brixCount} Brix-ændringer · ${res.data.skipped} sprunget over`
        );
      }
      if (!dryRun) {
        setPendingFile(null);
        fetchIngredients();
      } else {
        // Stash the file so admin can commit without re-picking it
        setPendingFile(file);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Import fejlede');
    } finally {
      setImporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Filen skal være en .csv');
      return;
    }
    await runImport(file, { dryRun: false });
  };

  const handleDryRunFile = async (e) => {
    const file = e.target.files?.[0];
    if (dryRunInputRef.current) dryRunInputRef.current.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Filen skal være en .csv');
      return;
    }
    await runImport(file, { dryRun: true });
  };

  const handleCommitPending = async () => {
    if (!pendingFile) return;
    await runImport(pendingFile, { dryRun: false });
  };

  const runMerge = async (dryRun) => {
    try {
      setMerging(true);
      setMergeResult(null);
      const res = await axios.post(
        `${API}/admin/ingredients/merge-duplicates`,
        { dry_run: dryRun },
        { withCredentials: true }
      );
      setMergeResult(res.data);
      if (dryRun) {
        toast.success(
          `Forhåndsvisning: ${res.data.duplicate_groups} dublet-grupper · ${res.data.merges_planned || 0} merges · ${res.data.deletes_planned || 0} sletninger (intet skrevet)`
        );
      } else {
        toast.success(
          `Merge færdig: ${res.data.merges_applied || 0} merges · ${res.data.deletes_applied || 0} sletninger`
        );
        fetchIngredients();
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Merge fejlede');
    } finally {
      setMerging(false);
    }
  };

  const handleMergePreview = () => runMerge(true);
  const handleMergeCommit = () => {
    // Open the backup prompt instead of going straight to merge.
    setShowBackupPrompt(true);
  };

  const handleBackupAndMerge = async () => {
    try {
      // Wait for the export to finish before kicking off the merge.
      await downloadCsv(
        '/admin/ingredients/export-csv',
        `slushbook-ingredients-before-merge-${new Date().toISOString().slice(0,10)}.csv`,
        true,  // throw on failure so we can abort the merge
      );
      toast.success('Backup downloadet — merger nu...');
    } catch (err) {
      console.error(err);
      toast.error('Backup fejlede — merge er afbrudt for din sikkerhed');
      setShowBackupPrompt(false);
      return;
    }
    setShowBackupPrompt(false);
    runMerge(false);
  };

  const handleSkipBackupAndMerge = () => {
    if (!window.confirm('Er du sikker på at du vil mergerne UDEN en backup? Det kan ikke fortrydes.')) return;
    setShowBackupPrompt(false);
    runMerge(false);
  };

  const categories = ['Sirup', 'Energidrik', 'Sodavand', 'Juice', 'Alkohol', 'Basis', 'Frugt', 'Grønt', 'Krydderi', 'Andet'];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-4xl font-bold mb-2">Admin: Ingredienser</h1>
        <p className="text-gray-600">
          Administrer master ingrediens-listen for "Hurtig Tilføjelse"
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-6">
          <input
            type="text"
            placeholder="Søg efter ingrediens eller kategori..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg"
          />
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleExport}
              variant="outline"
              className="border-cyan-500 text-cyan-700 hover:bg-cyan-50"
              data-testid="ingredients-export-csv"
            >
              <FaFileExport className="mr-2" />
              Eksporter CSV
            </Button>
            <Button
              onClick={() => dryRunInputRef.current?.click()}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              disabled={importing}
              data-testid="ingredients-dry-run-csv"
              title="Forhåndsvis hvad importen vil ændre — uden at skrive til databasen"
            >
              <FaEye className="mr-2" />
              Forhåndsvis import
            </Button>
            <input
              ref={dryRunInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleDryRunFile}
              className="hidden"
              data-testid="ingredients-dry-run-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="border-purple-500 text-purple-700 hover:bg-purple-50"
              disabled={importing}
              data-testid="ingredients-import-csv"
            >
              <FaFileImport className="mr-2" />
              {importing ? 'Importerer...' : 'Importer CSV'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportFile}
              className="hidden"
              data-testid="ingredients-import-file-input"
            />
            <Button
              onClick={handleDownloadTemplate}
              variant="outline"
              className="border-gray-400 text-gray-700 hover:bg-gray-50"
              data-testid="ingredients-download-template"
              title="Download eksempel-CSV"
            >
              <FaDownload className="mr-2" />
              Skabelon
            </Button>
            <Button
              onClick={handleSeedData}
              variant="outline"
              className="border-green-500 text-green-600 hover:bg-green-50"
            >
              <FaSeedling className="mr-2" />
              Importer Seed Data
            </Button>
            <Button
              onClick={handleMergePreview}
              variant="outline"
              className="border-rose-500 text-rose-700 hover:bg-rose-50"
              disabled={merging}
              data-testid="ingredients-merge-preview"
              title="Find duplikerede ingredienser og forhåndsvis en safe-merge plan"
            >
              <FaCompressArrowsAlt className="mr-2" />
              {merging ? 'Søger...' : 'Find dubletter'}
            </Button>
            <Button
              onClick={() => {
                setEditingIngredient(null);
                setFormData({ name: '', category: 'Sirup', category_key: '', default_brix: 0, keywords: '', match_aliases: '' });
                setShowDialog(true);
              }}
              className="bg-gradient-to-r from-blue-500 to-purple-500"
            >
              <FaPlus className="mr-2" />
              Tilføj Ingrediens
            </Button>
          </div>
        </div>

        {importResult && (
          <div
            className={`mb-4 p-4 rounded-xl border text-sm ${
              importResult.dry_run
                ? 'bg-amber-50 border-amber-300'
                : 'bg-gray-50 border-gray-200'
            }`}
            data-testid="ingredients-import-result"
          >
            <div className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
              {importResult.dry_run ? (
                <>
                  <FaEye className="text-amber-600" />
                  Forhåndsvisning (intet er skrevet til DB)
                </>
              ) : (
                <>Sidste import-resultat:</>
              )}
            </div>
            <div className="flex gap-4 flex-wrap text-gray-700">
              <span><span className="text-emerald-600 font-bold">{importResult.created}</span> {importResult.dry_run ? 'vil blive oprettet' : 'oprettet'}</span>
              <span><span className="text-blue-600 font-bold">{importResult.updated}</span> {importResult.dry_run ? 'vil blive opdateret' : 'opdateret'}</span>
              <span><span className="text-amber-600 font-bold">{importResult.brix_updated_count || 0}</span> Brix-ændringer</span>
              <span><span className="text-purple-600 font-bold">{importResult.alias_merged_count || 0}</span> alias-merges</span>
              <span><span className="text-teal-600 font-bold">{importResult.keyword_changed_count || 0}</span> keyword-ændringer</span>
              <span><span className="text-gray-600 font-bold">{importResult.skipped}</span> sprunget over</span>
              {importResult.errors?.length > 0 && (
                <span className="text-red-600 font-bold">{importResult.errors.length} fejl</span>
              )}
            </div>
            {importResult.dry_run && pendingFile && (
              <div className="mt-3 flex items-center gap-3">
                <Button
                  onClick={handleCommitPending}
                  disabled={importing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="ingredients-commit-import"
                >
                  <FaFileImport className="mr-2" />
                  {importing ? 'Importerer...' : 'Gennemfør import nu'}
                </Button>
                <Button
                  onClick={() => { setPendingFile(null); setImportResult(null); }}
                  variant="outline"
                  className="border-gray-400 text-gray-700"
                  data-testid="ingredients-cancel-pending"
                >
                  Annullér
                </Button>
                <span className="text-xs text-gray-500">Fil: {pendingFile.name}</span>
              </div>
            )}
            {!importResult.detected_brix_header && (
              <div
                className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2"
                data-testid="ingredients-import-no-brix-header"
              >
                Ingen Brix-kolonne fundet i CSV'en. Tilføj en kolonne kaldet
                <code className="mx-1 px-1 bg-white border rounded">standard_brix</code>
                (eller <code className="mx-1 px-1 bg-white border rounded">default_brix</code>) og prøv igen.
                <div className="mt-1 text-gray-700">
                  Headers set: {(importResult.headers_seen || []).join(', ') || '(ingen)'}
                </div>
              </div>
            )}
            {importResult.brix_updated_sample?.length > 0 && (
              <details className="mt-2 text-xs text-gray-700">
                <summary className="cursor-pointer font-semibold text-amber-700">
                  Vis Brix-ændringer ({importResult.brix_updated_count})
                </summary>
                <ul className="mt-1 list-disc list-inside max-h-40 overflow-auto">
                  {importResult.brix_updated_sample.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                  {importResult.brix_updated_count > importResult.brix_updated_sample.length && (
                    <li>… og {importResult.brix_updated_count - importResult.brix_updated_sample.length} mere</li>
                  )}
                </ul>
              </details>
            )}
            {importResult.created_sample?.length > 0 && (
              <details className="mt-2 text-xs text-gray-700">
                <summary className="cursor-pointer font-semibold text-emerald-700">
                  Vis nye ingredienser ({importResult.created})
                </summary>
                <ul className="mt-1 list-disc list-inside max-h-40 overflow-auto">
                  {importResult.created_sample.map((s, i) => (<li key={i}>{s}</li>))}
                  {importResult.created > importResult.created_sample.length && (
                    <li>… og {importResult.created - importResult.created_sample.length} mere</li>
                  )}
                </ul>
              </details>
            )}
            {importResult.updated_sample?.length > 0 && (
              <details className="mt-2 text-xs text-gray-700">
                <summary className="cursor-pointer font-semibold text-blue-700">
                  Vis opdaterede ingredienser ({importResult.updated})
                </summary>
                <ul className="mt-1 list-disc list-inside max-h-40 overflow-auto">
                  {importResult.updated_sample.map((s, i) => (<li key={i}>{s}</li>))}
                  {importResult.updated > importResult.updated_sample.length && (
                    <li>… og {importResult.updated - importResult.updated_sample.length} mere</li>
                  )}
                </ul>
              </details>
            )}
            {importResult.errors?.length > 0 && (
              <ul className="mt-2 text-xs text-red-700 list-disc list-inside max-h-32 overflow-auto">
                {importResult.errors.slice(0, 10).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
                {importResult.errors.length > 10 && <li>… og {importResult.errors.length - 10} mere</li>}
              </ul>
            )}
          </div>
        )}

        {mergeResult && (
          <div
            className={`mb-4 p-4 rounded-xl border text-sm ${
              mergeResult.dry_run ? 'bg-rose-50 border-rose-300' : 'bg-gray-50 border-gray-200'
            }`}
            data-testid="ingredients-merge-result"
          >
            <div className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
              <FaCompressArrowsAlt className="text-rose-600" />
              {mergeResult.dry_run
                ? 'Dublet-forhåndsvisning (intet er skrevet til DB)'
                : 'Dublet-merge afsluttet'}
            </div>
            <div className="flex gap-4 flex-wrap text-gray-700 mb-2">
              <span><span className="text-rose-600 font-bold">{mergeResult.duplicate_groups}</span> dublet-grupper</span>
              <span><span className="text-blue-600 font-bold">{mergeResult.merges_planned ?? mergeResult.merges_applied ?? 0}</span> {mergeResult.dry_run ? 'merges planlagt' : 'merges udført'}</span>
              <span><span className="text-amber-600 font-bold">{mergeResult.deletes_planned ?? mergeResult.deletes_applied ?? 0}</span> {mergeResult.dry_run ? 'dubletter slettes' : 'dubletter slettet'}</span>
              <span><span className="text-gray-600 font-bold">{mergeResult.total_docs}</span> ingredienser i alt</span>
            </div>

            {mergeResult.dry_run && mergeResult.duplicate_groups > 0 && (
              <div className="mt-2 flex items-center gap-3">
                <Button
                  onClick={handleMergeCommit}
                  disabled={merging}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="ingredients-merge-commit"
                >
                  <FaCompressArrowsAlt className="mr-2" />
                  {merging ? 'Slår sammen...' : 'Slå dubletter sammen nu'}
                </Button>
                <Button
                  onClick={() => setMergeResult(null)}
                  variant="outline"
                  className="border-gray-400 text-gray-700"
                >
                  Annullér
                </Button>
              </div>
            )}

            {mergeResult.affected_recipes?.length > 0 && (
              <details className="mt-3 text-xs text-amber-800">
                <summary className="cursor-pointer font-semibold">
                  ⚠️ {mergeResult.affected_recipes.length} opskrifter peger på et category_key der forsvinder
                </summary>
                <ul className="mt-1 list-disc list-inside max-h-40 overflow-auto">
                  {mergeResult.affected_recipes.slice(0, 30).map((r, i) => (
                    <li key={i}>
                      {r.name} → {r.dropped_keys.join(', ')}
                    </li>
                  ))}
                  {mergeResult.affected_recipes.length > 30 && (
                    <li>… og {mergeResult.affected_recipes.length - 30} mere</li>
                  )}
                </ul>
              </details>
            )}

            {mergeResult.plan?.length > 0 && (
              <details className="mt-2 text-xs text-gray-700" open={mergeResult.dry_run}>
                <summary className="cursor-pointer font-semibold text-rose-700">
                  Vis merge-plan ({mergeResult.plan.length} grupper)
                </summary>
                <div className="mt-2 space-y-3 max-h-96 overflow-auto">
                  {mergeResult.plan.map((p, i) => (
                    <div key={i} className="border border-rose-200 rounded p-2 bg-white">
                      <div className="font-semibold text-emerald-700">
                        BEHOLD: {p.keeper.name}
                        <span className="ml-2 text-gray-500 font-normal">
                          (key={p.keeper.category_key || '—'}, brix={p.keeper.default_brix ?? '—'}, aliaser={p.keeper.aliases_count})
                        </span>
                      </div>
                      {p.siblings.map((s, j) => (
                        <div key={j} className="text-rose-700 ml-3">
                          SLET: {s.name}
                          <span className="ml-2 text-gray-500 font-normal">
                            (key={s.category_key || '—'}, brix={s.default_brix ?? '—'}, aliaser={s.aliases_count})
                          </span>
                        </div>
                      ))}
                      <div className="text-blue-700 mt-1 ml-3">
                        ⇒ efter merge: brix={p.patch.default_brix ?? '—'}, aliaser={p.patch.aliases_after}, key={p.patch.category_key_after || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        <div className="text-sm text-gray-600 mb-4">
          {filteredIngredients.length} ingredienser
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold">Navn</th>
                <th className="text-left py-3 px-4 font-semibold">Kategori</th>
                <th className="text-left py-3 px-4 font-semibold">Match-aliaser</th>
                <th className="text-left py-3 px-4 font-semibold">Standard Brix</th>
                <th className="text-right py-3 px-4 font-semibold">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {filteredIngredients.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-gray-500">
                    Ingen ingredienser fundet
                  </td>
                </tr>
              ) : (
                filteredIngredients.map((ingredient) => {
                  const aliases = Array.isArray(ingredient.match_aliases) ? ingredient.match_aliases : [];
                  return (
                  <tr key={ingredient.id} data-ingredient-row={ingredient.id} className="border-b border-gray-100 hover:bg-gray-50 transition-shadow">
                    <td className="py-3 px-4 font-medium">
                      {ingredient.name}
                      {ingredient.category_key && (
                        <div className="text-xs text-gray-400 font-mono mt-0.5">{ingredient.category_key}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {ingredient.category || 'N/A'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {aliases.length === 0 ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {aliases.slice(0, 3).map((a, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-full">
                              {a}
                            </span>
                          ))}
                          {aliases.length > 3 && (
                            <span className="text-xs text-gray-500 self-center">+{aliases.length - 3}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">{ingredient.default_brix || 0}%</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleEdit(ingredient)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(ingredient.id, ingredient.name)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">
              {editingIngredient ? 'Rediger Ingrediens' : 'Tilføj Ingrediens'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Navn</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="F.eks. Jordbær sirup"
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Kategori</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Category key
                  <span className="text-xs text-gray-500 ml-2">Valgfrit — fx <code>sirup.blue</code></span>
                </label>
                <input
                  type="text"
                  value={formData.category_key}
                  onChange={(e) => setFormData({ ...formData, category_key: e.target.value })}
                  placeholder="F.eks. sirup.blue"
                  className="w-full px-4 py-2 border rounded-lg font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Bruges af Match-Finder til at koble pantry-items med opskrift-ingredienser.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Standard Brix (%)</label>
                <input
                  type="number"
                  value={formData.default_brix}
                  onChange={(e) => setFormData({ ...formData, default_brix: parseFloat(e.target.value) || 0 })}
                  placeholder="0-100"
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Match-aliaser
                  <span className="text-xs text-gray-500 ml-2">Komma-separeret</span>
                </label>
                <textarea
                  value={formData.match_aliases}
                  onChange={(e) => setFormData({ ...formData, match_aliases: e.target.value })}
                  placeholder="F.eks. blå curaçao sirup, blue curacao, curaçao sirup, blå farvestof, blå sirup"
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Match-Finder finder opskrifter hvor ingrediensen kan bruges, også hvis opskriften bruger en anden formulering.
                  <br />
                  <strong>Regel:</strong> Brede enkeltord som "blå" eller "blue" matcher <em>ikke</em> alene — kun fraser med mellemrum eller enkeltord med ≥ 6 tegn (fx "curaçao") tæller.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Keywords (søgeord)
                  <span className="text-xs text-gray-500 ml-2">Valgfrit - komma-separeret</span>
                </label>
                <input
                  type="text"
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder="F.eks. Fanta, Appelsin sodavand, Orange"
                  className="w-full px-4 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tilføj produktnavne og alternative søgeord (komma-separeret)
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500"
                >
                  {saving ? 'Gemmer...' : (editingIngredient ? 'Opdater' : 'Opret')}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowDialog(false);
                    setEditingIngredient(null);
                    setFormData({ name: '', category: 'Sirup', category_key: '', default_brix: 0, keywords: '', match_aliases: '' });
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={saving}
                >
                  Annuller
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBackupPrompt && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="ingredients-backup-prompt"
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <FaCompressArrowsAlt className="text-rose-600 text-2xl mt-1" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Eksporter backup først?</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Du er ved at slå {mergeResult?.merges_planned || 0} dublet-grupper sammen og slette{' '}
                  {mergeResult?.deletes_planned || 0} dublet-dokumenter. Dette kan <strong>ikke fortrydes</strong>.
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  Vi anbefaler at downloade en CSV-backup af alle ingredienser inden — så kan du altid importere den tilbage hvis noget går galt.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleBackupAndMerge}
                disabled={merging}
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                data-testid="ingredients-backup-and-merge"
              >
                <FaDownload className="mr-2" />
                Download backup og merge
              </Button>
              <Button
                onClick={handleSkipBackupAndMerge}
                disabled={merging}
                variant="outline"
                className="border-rose-400 text-rose-700 hover:bg-rose-50 w-full"
                data-testid="ingredients-skip-backup-merge"
              >
                Merge uden backup
              </Button>
              <Button
                onClick={() => setShowBackupPrompt(false)}
                disabled={merging}
                variant="outline"
                className="border-gray-300 text-gray-600 w-full"
                data-testid="ingredients-cancel-backup-prompt"
              >
                Annullér
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminIngredientsPage;
